import {
  addItemToGroup,
  CustomFilterState,
  FilterTypes,
  FormattedDevice,
  FormattedSession,
  getEmptyGroup,
  getFilterState,
  HardwareStates,
  runCheck
} from '@frontend/core'
import {
  deviceTypes,
  DeviceTypeValues
} from '@frontend/core/lib/domains/hardware'
import { useContext, useEffect, useMemo } from 'react'
import { useFilterReducer } from '../../modules/filter/filter'
import { SessionContext } from '../sessions'
import { useFormattedHardware } from './hooks'

export type HardwareFilterTypeKeys =
  | 'id'
  | 'assigned'
  | 'status'
  | 'type'
  | 'team'

export type HardwareFilters = {
  id: FilterTypes<string, 'id', HardwareFilterTypeKeys>
  assigned: FilterTypes<
    'assigned' | 'notAssigned',
    'assigned',
    HardwareFilterTypeKeys
  >
  status: FilterTypes<HardwareStates, 'status', HardwareFilterTypeKeys>
  type: FilterTypes<DeviceTypeValues, 'type', HardwareFilterTypeKeys>
  team: FilterTypes<string, 'team', HardwareFilterTypeKeys>
  onPitch: FilterTypes<'on' | 'off' | 'All', 'onPitch', HardwareFilterTypeKeys>
  charging: FilterTypes<
    'charging' | 'notCharging' | 'fullCharge',
    'charging',
    HardwareFilterTypeKeys
  >
}

export const defaultHardwareFilterState: HardwareFilters = {
  id: {
    key: 'id',
    label: 'Search by ID',
    type: 'searchStr',
    value: '',
    options: []
  },
  assigned: {
    key: 'assigned',
    label: 'Assigned',
    type: 'multiple',
    options: [
      { name: 'Assigned', value: 'assigned' },
      { name: 'Not Assigned', value: 'notAssigned' }
    ],
    value: null
  },
  status: {
    key: 'status',
    label: 'Status',
    type: 'multiple',
    options: [
      { name: 'Online', value: 'online' },
      { name: 'Sleep', value: 'sleep' },
      { name: 'Offline', value: 'offline' }
    ],
    value: null
  },
  type: {
    key: 'type',
    label: 'Type',
    type: 'multiple',
    options: deviceTypes.options,
    value: null
  },
  team: {
    key: 'team',
    label: 'Team',
    type: 'multiple',
    options: [],
    value: null
  },
  onPitch: {
    key: 'onPitch',
    label: 'On Pitch',
    type: 'single',
    options: [
      { name: 'All', value: 'All' },
      { name: 'On Pitch', value: 'on' }
    ],
    value: null
  },
  charging: {
    key: 'charging',
    label: 'Charging',
    type: 'multiple',
    options: [
      { name: 'Charging', value: 'charging' },
      { name: 'Not Charging', value: 'notCharging' },
      { name: 'Full Charge', value: 'fullCharge' }
    ],
    value: null
  }
}

export function getHardwareFilterData(
  device: FormattedDevice,
  formattedSession?: FormattedSession
) {
  const playerSession =
    formattedSession?.playersSessions.byHardwareId.map[device.id] || null

  // TODO: improve charging state handling
  // Do we still need trickle charging? And should this also be a filter option?
  let charging = 'notCharging'
  if (
    device.chargingState === 'charging' ||
    device.chargingState === 'trickleCharging'
  ) {
    charging = 'charging'
  } else if (device.chargingState === 'fullCharge') {
    charging = 'fullCharge'
  }

  return {
    ...device,
    assigned: playerSession ? 'assigned' : 'notAssigned',
    status: device.metrics.status.value,
    type: device.type.value,
    team: playerSession?.teamId || null,
    onPitch: device.metrics.inPitch.value ? 'on' : 'off',
    charging
  }
}

export const useHardwareFilter = (
  customFilterState: CustomFilterState<HardwareFilters>
) => {
  const formattedHardware = useFormattedHardware()

  const { formattedSession } = useContext(SessionContext)

  const filterState = useMemo(
    () => getFilterState(customFilterState, defaultHardwareFilterState),
    []
  )
  // TODO: update this filter type to use the filter reducer properly //
  const { filters, updateFilterValue, updateFilterOptions } =
    useFilterReducer(filterState)

  useEffect(() => {
    updateFilterOptions('team', formattedSession?.teams.options)
  }, [formattedSession])

  const filteredHardware = useMemo(() => {
    const filteredHardware = getEmptyGroup<FormattedDevice, string>()

    formattedHardware.devices.list
      .filter((device) => {
        const data = getHardwareFilterData(device, formattedSession)

        return Object.keys(filters).every((filterKey) => {
          const filter = filters[filterKey as keyof typeof filters]
          const itemValue = data[filterKey as keyof typeof data]
          return runCheck(filter, itemValue)
        })
      })
      // Add item to group
      .forEach((device) => {
        // Add item to all group
        addItemToGroup(
          filteredHardware,
          device,
          device.id.toString(),
          'id',
          device.id
        )
      })

    return filteredHardware
  }, [formattedSession, formattedHardware, filters])

  return {
    filteredHardware,
    filters,
    updateFilterValue
  }
}
